import { useEffect, useState } from 'react'
import { Order, TimelineEvent } from '../types'
import { authFetch } from '../utils/authFetch'

interface OrderDetailModalProps {
  order: Order | null
  onClose: () => void
}

function formatDate(dateStr: string): string {
  if (!dateStr || dateStr.length !== 8) return dateStr || '-'
  return dateStr.substring(6, 8) + '/' + dateStr.substring(4, 6) + '/' + dateStr.substring(0, 4)
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0)
}

function formatEventTime(dateStr: string): string {
  if (!dateStr) return '-'
  try {
    const d = new Date(dateStr)
    const date = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    const time = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    return `${date} ${time}`
  } catch {
    return dateStr
  }
}

function getShopDomain(): string {
  return localStorage.getItem('shop_domain') || ''
}

export function OrderDetailModal({ order, onClose }: OrderDetailModalProps) {
  const [events, setEvents] = useState<TimelineEvent[]>([])
  const [eventsLoading, setEventsLoading] = useState(false)
  const [eventsError, setEventsError] = useState<string | null>(null)
  const [productsByStory, setProductsByStory] = useState<Record<string, any[]>>({})

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }

    if (order) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
      loadWiddeEvents(order.widdeUid, order.date)
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [order, onClose])

  async function loadWiddeEvents(widdeUid: string, _date: string) {
    if (!widdeUid || widdeUid === '(not set)') {
      setEvents([])
      return
    }

    setEventsLoading(true)
    setEventsError(null)
    setProductsByStory({})

    try {
      const res = await authFetch(`/api/events/${encodeURIComponent(widdeUid)}`)
      const data = await res.json()

      if (!res.ok) throw new Error(data.error || 'Erro ao buscar eventos')

      setEvents(data)

      // Carregar produtos para story_view events
      const domain = getShopDomain()
      if (domain) {
        const storyEvents = data.filter((e: TimelineEvent) => e.event_name === 'story_view' && e.story_key)
        const uniqueStoryKeys = Array.from(new Set(storyEvents.map((e: TimelineEvent) => e.story_key!))) as string[]

        const productsMap: Record<string, any[]> = {}
        await Promise.all(
          uniqueStoryKeys.map(async (storyKey) => {
            try {
              const res = await authFetch(`/api/products/${encodeURIComponent(storyKey)}?domain=${encodeURIComponent(domain)}`)
              const products = await res.json()
              if (Array.isArray(products) && products.length > 0) {
                productsMap[storyKey] = products
              }
            } catch {}
          })
        )
        setProductsByStory(productsMap)
      }
    } catch (err: any) {
      console.error('Erro ao carregar eventos:', err)
      setEvents([])
      setEventsError(err?.message || 'Erro ao carregar eventos')
    } finally {
      setEventsLoading(false)
    }
  }

  if (!order) return null

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose()
  }

  return (
    <div className="modal-overlay active" onClick={handleOverlayClick}>
      <div className="modal-content">
        <div className="modal-header">
          <h2>Detalhes do Pedido</h2>
          <button className="modal-close" onClick={onClose} type="button">&times;</button>
        </div>

        <div className="modal-body">
          {/* Header */}
          <div className="detail-header">
            <div className="detail-stat">
              <div className="label">Transaction ID</div>
              <div className="value">{order.transactionId}</div>
            </div>
            <div className="detail-stat">
              <div className="label">Data</div>
              <div className="value">{formatDate(order.date)}</div>
            </div>
            <div className="detail-stat">
              <div className="label">Widde UID</div>
              <div className="value">{order.widdeUid || '(not set)'}</div>
            </div>
            <div className="detail-stat">
              <div className="label">Receita Total</div>
              <div className="value revenue">{formatCurrency(order.purchaseRevenue)}</div>
            </div>
          </div>

          {/* Itens */}
          <h3>Itens do Pedido</h3>
          <table className="items-table">
            <thead>
              <tr>
                <th>Item ID</th>
                <th>Nome</th>
                <th>Quantidade</th>
                <th>Receita</th>
              </tr>
            </thead>
            <tbody>
              {order.items.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', color: '#9ca3af', padding: 24 }}>
                    Nenhum item encontrado
                  </td>
                </tr>
              ) : (
                order.items.map((item, index) => (
                  <tr key={index}>
                    <td><code>{item.itemId}</code></td>
                    <td>{item.itemName}</td>
                    <td>{item.itemsPurchased}</td>
                    <td className="revenue">{formatCurrency(item.itemRevenue)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Timeline */}
          <div className="timeline-section" style={{ marginTop: 24 }}>
            <h3>
              Timeline de Eventos Widde
              {events.length > 0 && (
                <span className="events-count">({events.length} eventos)</span>
              )}
            </h3>

            <div id="timelineContent">
              {eventsLoading ? (
                <p className="timeline-loading">
                  <span className="spinner" /> Carregando eventos do ClickHouse...
                </p>
              ) : !order.widdeUid || order.widdeUid === '(not set)' ? (
                <p className="timeline-empty">
                  Este pedido nao possui Widde UID. Nao e possivel carregar eventos.
                </p>
              ) : eventsError ? (
                <p className="timeline-empty">
                  Erro ao carregar eventos: {eventsError}
                </p>
              ) : events.length === 0 ? (
                <p className="timeline-empty">
                  Nenhum evento Widde encontrado para este usuario nesta data.
                </p>
              ) : (
                <div className="timeline">
                  {events.map((event, index) => {
                    const details: { label: string; value: string }[] = []
                    if (event.session_key) details.push({ label: 'Session', value: event.session_key })
                    if (event.story_key) details.push({ label: 'Story', value: event.story_key })
                    if (event.collection_key) details.push({ label: 'Collection', value: event.collection_key })
                    if (event.media_key) details.push({ label: 'Media', value: event.media_key })
                    if (event.product_key) details.push({ label: 'Product', value: event.product_key })

                    let propsObj: Record<string, any> | null = null
                    if (event.properties) {
                      try {
                        propsObj = typeof event.properties === 'string'
                          ? JSON.parse(event.properties)
                          : event.properties
                        if (propsObj && Object.keys(propsObj).length === 0) propsObj = null
                      } catch {
                        propsObj = null
                      }
                    }

                    const products = event.event_name === 'story_view' && event.story_key
                      ? productsByStory[event.story_key] || []
                      : []

                    return (
                      <div key={index} className="timeline-event">
                        <div className="event-header">
                          <span className="event-name">{event.event_name}</span>
                          <span className="event-time">{formatEventTime(event.event_at)}</span>
                        </div>

                        {details.length > 0 && (
                          <div className="event-details">
                            {details.map((d, i) => (
                              <div key={i} className="event-detail">
                                {d.label}: <span>{d.value}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {propsObj && (
                          <div className="event-props">
                            {JSON.stringify(propsObj, null, 2)}
                          </div>
                        )}

                        {products.length > 0 && (
                          <div style={{ marginTop: 10 }}>
                            <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#6b7280', marginBottom: 6, fontWeight: 600 }}>
                              Produtos
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              {products.map((p: any, pi: number) => (
                                <div key={pi} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 8, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 13 }}>
                                  {p.mainImage && (
                                    <img src={p.mainImage} style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} alt="" />
                                  )}
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 500, color: '#111', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {p.title || ''}
                                    </div>
                                    <div style={{ color: '#6b7280', fontSize: 12 }}>
                                      ID: {String(p.externalId || '')}
                                    </div>
                                  </div>
                                  <div style={{ fontWeight: 600, color: '#059669', whiteSpace: 'nowrap' }}>
                                    {p.priceFormatted || ''}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
